/**
 * Customer card registry (BE-8, BE-8b) — real API.
 *
 * Portal side: a customer's own saved cards (metadata only — never the full number or CVV), sent with the portal token.
 * Staff side: one customer's active cards (payment pickers and the client page), the TC adding / editing / removing
 * them, and the audit trail of every card change from either side.
 */

import ApiService from '../api.service';
import { keysCamelToSnake, unwrapData, unwrapList } from '../../utils/apiConvert';

const portal = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('portalToken')}` } });

export const customerCardService = {
  getCards: async () => unwrapList(await ApiService.get('/api/portal/cards', portal())).items,

  // data: { nickname, cardType, lastFour, bankIssuer, cardholderName, expiry? (MM/YY) }
  createCard: async (data) => unwrapData(await ApiService.post('/api/portal/cards', keysCamelToSnake(data), portal())),

  // Only nickname and expiry change.
  updateCard: async (id, data) => unwrapData(await ApiService.put(`/api/portal/cards/${id}`, keysCamelToSnake(data), portal())),

  removeCard: async (id) => ApiService.delete(`/api/portal/cards/${id}`, portal()),

  // What was paid with the customer's own card; only the payments that stand. params: { from?, to? }
  getTransactions: async (id, params) => unwrapList(await ApiService.get(`/api/portal/cards/${id}/transactions`, { params, ...portal() })).items,

  // Staff: a customer's active cards, each with addedBy (PORTAL / TC).
  getForOrganization: async (orgId) =>
    unwrapList(await ApiService.get('/api/customer-cards', { params: { orgId } })).items,

  // Staff: every payment made with one of the customer's cards, whatever its status. params: { from?, to? }
  getTransactionsForCard: async (id, params) => unwrapList(await ApiService.get(`/api/customer-cards/${id}/transactions`, { params })).items,

  listForOrganization: async (orgId) => customerCardService.getForOrganization(orgId),
  // data: same as createCard
  createForOrganization: async (orgId, data) =>
    unwrapData(await ApiService.post('/api/customer-cards', keysCamelToSnake({ ...data, orgId }))),
  // Only nickname and expiry change.
  updateForOrganization: async (orgId, id, data) =>
    unwrapData(await ApiService.put(`/api/customer-cards/${id}`, keysCamelToSnake(data))),
  removeForOrganization: async (orgId, id) => ApiService.delete(`/api/customer-cards/${id}`),
  // Who added / changed / removed which card, newest first, removed cards included:
  // [{ id, cardId, cardLabel, occurredAt, actorName, actorType (TC_USER | PORTAL_USER), action, details }]
  activityForOrganization: async (orgId) =>
    unwrapList(await ApiService.get('/api/customer-cards/activity', { params: { orgId } })).items,
};

export default customerCardService;
